import { Contract, PAGES } from "@dune/types";
import { Input, PaginatedDataTable, useSidebar } from "@dune/ui";
import { SearchSm } from "@untitled-ui/icons-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContracts } from "../../../../../api/queries/contracts.queries";
import { contractsColumns } from "../../../../contracts/components/contractsColumns";
import { usePagination } from "../../../../../hooks/usePagination";

const HIDE_BROKER_COLUMN = true;

type BrokerContractsTabProps = {
	brokerSocietyId: number;
};

const BrokerContractsTab: React.FC<BrokerContractsTabProps> = ({
	brokerSocietyId,
}) => {
	const navigate = useNavigate();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";
	const { page, limit, handlePageChange, handleLimitChange } = usePagination();
	const [searchInput, setSearchInput] = useState("");

	const filters = useMemo(
		() => ({
			brokerSocietyId,
			page,
			limit,
			...(searchInput.trim() && { search: searchInput.trim() }),
		}),
		[brokerSocietyId, page, limit, searchInput],
	);

	const { data: contractsResponse, isLoading, error } = useContracts(filters);

	const contracts = contractsResponse?.data ?? [];
	const paginationMeta = contractsResponse?.meta ?? {
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const handleRowClick = (contract: Contract) => {
		navigate(PAGES.CONTRACTS.show.withParams(contract.id.toString()));
	};

	const columns = contractsColumns(HIDE_BROKER_COLUMN);

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-red-600">
				Erreur lors du chargement des contrats.
			</div>
		);
	}

	return (
		<div className="space-y-4 flex-1 min-h-0 flex flex-col">
			<div className="flex flex-row gap-2 w-full max-w-md">
				<Input
					type="text"
					placeholder="Recherche"
					value={searchInput}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setSearchInput(e.target.value)
					}
					iconOrText={<SearchSm />}
					iconOrTextPosition="right"
				/>
			</div>
			<div className="flex-1 min-h-0 w-full overflow-auto">
				<div className={isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}>
					<PaginatedDataTable
						data={contracts}
						columns={columns}
						meta={paginationMeta}
						onPageChange={handlePageChange}
						onLimitChange={handleLimitChange}
						isLoading={isLoading}
						onRowClick={handleRowClick}
						noResultsText="Aucun contrat trouvé"
					/>
				</div>
			</div>
		</div>
	);
};

export default BrokerContractsTab;
